import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ContentApi } from "../../../../shared/api/contentApi";
import ParameterEditDialog from "../components/ParameterEditDialog";
import ParameterEditStr from "../components/SystemNameEditParameter";
import DetailPageHero from "../../../../components/ui/DetailPageHero";
import DetailPageSidebar from "../../../../components/ui/DetailPageSidebar";
import DetailTabs, { DetailTabsContent } from "../../../../components/ui/DetailTabs";
import { Info, Database } from "lucide-react";
import { useContentContext } from "../../ContentContext";
import { notify } from "shared/utils/notify";
import formatDate from "../../../../shared/helpers/formatDate";
import { Select, Flex, Modal, Card, Button, Splitter, Divider } from "antd";
import { DETAIL_CARD_CLASS, DETAIL_FIELD_CLASS } from "../../../../shared/constants/detailLayout";

// Helper component for JSON display
function Jsonish({ value }) {
    if (value === null || value === undefined) return <span className="text-zinc-400">null</span>;
    if (typeof value === "string") return <span className="text-green-600">"{value}"</span>;
    if (typeof value === "number") return <span className="text-blue-600">{value}</span>;
    if (typeof value === "boolean")
        return <span className="text-purple-600">{value.toString()}</span>;
    return <pre className="text-xs">{JSON.stringify(value, null, 2)}</pre>;
}

export default function ParameterDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canMutate, selectedVersion, projectId, versionId } = useContentContext();
    const [parameter, setParameter] = useState(null);
    const [valueType, setValueType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [editOpen, setEditOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [groups, setGroups] = useState([]);
    const [attachGroupId, setAttachGroupId] = useState(undefined);
    const [attachingGroup, setAttachingGroup] = useState(false);

    const loadDetails = async (signal) => {
        if (!projectId || !versionId || !id) {
            setParameter(null);
            setGroups([]);
            setLoading(false);
            setValueType(null);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const [parameterData, groupList] = await Promise.all([
                ContentApi.getParameterById(projectId, versionId, id, { signal }),
                ContentApi.getParameterGroups({ projectId, versionId, page: 1, pageSize: 1000 }, { signal }),
            ]);
            const valueTypeData = await ContentApi.getValueTypeById(parameterData.valueTypeId)
            setParameter(parameterData || null);
            setGroups(Array.isArray(groupList?.items) ? groupList.items : []);
            setValueType(valueTypeData || null);
        } catch (e) {
            if (!signal?.aborted) {
                setError(e?.message || "Ошибка загрузки");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    // Find groups that used parameter by id. Groups.parameterIds contains array of parameter ids. If parameter id is in group.parameterIds, then this group uses the parameter.
    const groupsUsingParameter = groups.filter(group => group.parameterIds?.includes(id));


    useEffect(() => {
        const ac = new AbortController();
        loadDetails(ac.signal);

        return () => ac.abort();
    }, [id, projectId, versionId]);

    const handleDelete = async () => {
        try {
            await ContentApi.deleteParameter(projectId, versionId, id);
            notify.success("Параметр удалён");
            navigate("/content/parameters");
        } catch (err) {
            notify.error(`Ошибка удаления: ${err.message}`);
        }
    };

    const handleAttachToGroup = async () => {
        if (!canMutate || !attachGroupId || !id) return;
        setAttachingGroup(true);
        try {
            await ContentApi.attachParameterToGroup(projectId, versionId, attachGroupId, { parameterId: id });
            setAttachGroupId(undefined);
            notify.success("Параметр присоединён к группе");
        } catch (err) {
            notify.error(err?.message || "Не удалось присоединить параметр к группе");
        } finally {
            setAttachingGroup(false);
        }
    };

    const tabs = [
        { value: "overview", label: "Обзор", icon: <Info size={16} /> },
        { value: "usage", label: "Использование", icon: <Database size={16} /> },
        { value: "meta", label: "Метаданные", icon: <Info size={16} /> },
    ];

    if (loading) {
        return (
            <section className="p-2 md:p-4">
                <div className="p-4 text-sm text-zinc-500">Загрузка…</div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="p-2 md:p-4">
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            </section>
        );
    }

    if (!parameter) {
        return (
            <section className="p-2 md:p-4">
                <div className="p-4 text-sm text-zinc-500">Не найдено</div>
            </section>
        );
    }

    return (
        <section className="w-full">
            <div className="mx-auto w-full max-w-6xl">
                {!canMutate && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 p-3 text-sm mb-4">
                        Версия {selectedVersion?.status || "unknown"} только для чтения. Используйте Fork в draft для изменений.
                    </div>
                )}

                <Flex vertical gap="large" className="mt-2 w-full">
                    <div className="w-full">
                        <DetailTabs defaultValue="overview" tabs={tabs} onDelete={canMutate ? () => setDeleteDialogOpen(true) : undefined}>
                            {/* Overview Tab */}
                            <DetailTabsContent value="overview" className="space-y-2">
                                <div className="space-y-2">
                                    <DetailPageHero
                                        title={parameter.name}
                                        subtitle={parameter.systemName}
                                        breadcrumbs={[
                                            { label: "Параметры", to: "/content/parameters" },
                                            { label: "Детали" },
                                        ]}
                                        metadata={[
                                            { label: "ID", value: `${parameter.id || parameter.parameterId}` },
                                            { label: "Тип", value: `${valueType?.code || parameter.valueTypeCode || "Загрузка..."}` },
                                        ]}
                                    />
                                    <ParameterEditStr
                                        parameter={parameter}
                                        projectId={projectId}
                                        versionId={versionId}
                                        onClose={() => setEditOpen(false)}
                                        onSaved={async (updated) => {
                                            setParameter(updated || parameter);
                                            await loadDetails();
                                            setEditOpen(false);
                                        }}
                                    />
                                    <Card title="Дополнительно" className={DETAIL_CARD_CLASS}>
                                        <div className="">
                                            <h2 className="eyebrow mb-1">Значение по умолчанию</h2>
                                            {parameter?.defaultValueJson ? (
                                                <p className="text-sm">{parameter.defaultValueJson}</p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">-</p>
                                            )}
                                        </div>
                                        <div className="mt-2">
                                            <h2 className="eyebrow mb-2">optionsJson</h2>
                                            {parameter?.optionsJson ? (
                                                <p className="text-sm">{parameter.optionsJson}</p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">-</p>
                                            )}
                                        </div>
                                        <div className="mt-2">
                                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                                <div>Создана: {parameter?.createdAt ? formatDate(parameter.createdAt) : "-"}</div>
                                                <div>
                                                    Обновлено: {parameter?.updatedAt ? formatDate(parameter.updatedAt) : parameter?.createdAt ? formatDate(parameter.createdAt) : "-"}
                                                </div>
                                            </div>
                                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                                <div>Создана кем: {parameter?.createdBy ?? "-"}</div>
                                                <div>
                                                    Обновлено кем: {parameter?.updatedBy ?? "-"}
                                                </div>
                                            </div>
                                        </div>
                                    </Card>

                                    <Card title="ValueType" className={DETAIL_CARD_CLASS}>
                                        <div className="">
                                            <h2 className="eyebrow mb-1">Название</h2>
                                            {valueType?.name ? (
                                                <p className="text-sm">{valueType?.name}</p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">-</p>
                                            )}

                                            <h2 className="eyebrow mb-1 mt-2">Код</h2>
                                            {valueType?.code ? (
                                                <p className="text-sm">{valueType?.code}</p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">-</p>
                                            )}

                                            <h2 className="eyebrow mb-1 mt-2">ValueKind</h2>
                                            {valueType?.valueKind ? (
                                                <p className="text-sm">{valueType?.valueKind}</p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">-</p>
                                            )}

                                            <h2 className="eyebrow mb-1 mt-2">valueSchemaJson</h2>
                                            {valueType?.valueSchemaJson ? (
                                                <p className="text-sm">{valueType?.valueSchemaJson}</p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">-</p>
                                            )}

                                            <h2 className="eyebrow mb-1 mt-2">optionsSchemaJson</h2>
                                            {valueType?.optionsSchemaJson ? (
                                                <p className="text-sm">{valueType?.optionsSchemaJson}</p>
                                            ) : (
                                                <p className="text-sm text-zinc-500">-</p>
                                            )}
                                        </div>
                                    </Card>
                                </div>
                            </DetailTabsContent>
                            <DetailTabsContent value="usage" className="space-y-2">
                                <Card title="Присоединить параметр к группе" className={DETAIL_CARD_CLASS}>
                                    <h2 className="eyebrow mb-1">Использование параметра</h2>
                                    <p className="text-sm text-zinc-500 mb-3">
                                        Присоедините параметр к группе параметров.
                                    </p>
                                    <Flex gap="small" align="center" wrap>
                                        <Select
                                            className={DETAIL_FIELD_CLASS}
                                            value={attachGroupId}
                                            onChange={setAttachGroupId}
                                            placeholder={groups.length ? "Выберите группу" : "Нет доступных групп"}
                                            disabled={!canMutate || attachingGroup || groups.length === 0}
                                            options={groups.map((group) => ({
                                                label: group.name || group.systemName || group.id,
                                                value: group.id,
                                            }))}
                                        />
                                        <Button
                                            type="primary"
                                            onClick={handleAttachToGroup}
                                            loading={attachingGroup}
                                            disabled={!canMutate || attachingGroup || !attachGroupId}
                                        >
                                            Присоединить
                                        </Button>
                                    </Flex>
                                </Card>
                                <Card title="Использование параметра" className={DETAIL_CARD_CLASS}>
                                    {/* информация о группе */}
                                    {groupsUsingParameter?.length ? (
                                        groupsUsingParameter.map((group) => (
                                        <div className="mb-2" key={group.id}>
                                            <div className="text-sm">
                                                <strong>Название группы:</strong> {group.name || "-"}
                                            </div>
                                            <div className="text-sm">
                                                <strong>System Name:</strong> {group.systemName || "-"}
                                            </div>
                                            <div className="text-sm">
                                                <strong>Id:</strong> <Link to={`/content/group-parameters/${group.id}`}>{group.id || "-"}</Link>
                                            </div>
                                            <Divider className="my-2" />
                                        </div>
                                        ))
                                    ) : (
                                        <div className="mt-2 text-sm text-zinc-500">
                                            Параметр не привязан ни к одной группе.
                                        </div>
                                    )}
                                </Card>
                            </DetailTabsContent>

                            <DetailTabsContent value="meta" className="space-y-4">
                                <div className="space-y-4">
                                    <Card title="Метаданные" className={DETAIL_CARD_CLASS}>
                                        <div className="">
                                            <h2 className="eyebrow mb-2">Id</h2>
                                            <p className="text-sm">{parameter?.id || parameter?.parameterId}</p>
                                        </div>
                                        <div className="mt-4">
                                            <h2 className="eyebrow">NameTrId</h2>
                                            <div className="text-sm mt-0.5">{parameter?.nameTrId || '-'}</div>
                                        </div>
                                        <div className="mt-4">
                                            <h2 className="eyebrow">DescriptionTrId</h2>
                                            <div className="text-sm mt-0.5">{parameter?.descriptionTrId || '-'}</div>
                                        </div>
                                        <div className="mt-4">
                                            <h2 className="eyebrow">Тип значения</h2>
                                            <div className="mt-0.5">
                                                <span className="chip chip-muted">{valueType?.code || "Загрузка.."}</span>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <h2 className="eyebrow">ValueTypeId</h2>
                                            <div className="text-sm mt-0.5">{parameter?.valueTypeId || valueType?.id || '-'}</div>
                                        </div>
                                        <div className="mt-4">
                                            <h2 className="eyebrow">OrderKey</h2>
                                            <div className="text-sm mt-0.5">{parameter?.orderKey || '-'}</div>
                                        </div>
                                        {parameter?.defaultValue !== null &&
                                            parameter?.defaultValue !== undefined && (
                                                <div className="mt-4">
                                                    <h2 className="eyebrow">Значение по умолчанию</h2>
                                                    <div className="text-sm mt-0.5">
                                                        <Jsonish value={parameter?.defaultValueJson || "Загрузка.."} />
                                                    </div>
                                                </div>
                                            )}
                                    </Card>
                                </div>
                            </DetailTabsContent>
                        </DetailTabs>
                    </div>
                </Flex>

                {/* Edit Dialog */}
                <ParameterEditDialog
                    open={editOpen}
                    parameter={parameter}
                    projectId={projectId}
                    versionId={versionId}
                    onClose={() => setEditOpen(false)}
                    onSaved={async (updated) => {
                        setParameter(updated || parameter);
                        await loadDetails();
                        setEditOpen(false);
                    }}
                />

                {/* Delete Confirmation Dialog */}
                <Modal
                    open={deleteDialogOpen}
                    onCancel={() => setDeleteDialogOpen(false)}
                    title="Удалить параметр?"
                    closable={false}
                    centered
                    width=""
                    className=""
                    styles={{
                        body: { padding: 0 },
                        content: { overflow: "hidden" },
                    }}
                    onOk={() => { setDeleteDialogOpen(false); handleDelete(); }}
                    footer={(_, { OkBtn, CancelBtn }) => (
                        <>
                            <OkBtn />
                            <CancelBtn />
                        </>
                    )}
                >
                    <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                        Внимание! Удаление параметра может привести к ошибкам, которые его используют.
                    </div>
                    <p>
                        <strong>Параметр:</strong> {parameter?.name}
                    </p>
                    <p className="mt-1">
                        <strong>System Name:</strong> {parameter?.systemName}
                    </p>
                </Modal>
            </div>
        </section>
    );
}